import { useCallback, useState } from "react";

import { Info, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { DocumentCategory } from "@/constants/documents";
import { FILE_EXTENSIONS, FILE_SIZES } from "@/constants/file";
import { setMessage } from "@/redux/notification";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { ReactComponent as DocumentIcon } from "@assets/svg/document-filled.svg";
import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { Button } from "@components/ui/button";
import { Card } from "@components/ui/card";
import { Checkbox } from "@components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { useRemoveFile } from "@hooks/mutations/use-files";
import { useFileUpload } from "@hooks/useFileUpload";

import { UploadButton } from "./UploadButton";

// Document category item type definitions
type CategoryItemDescription = {
  id: string;
  value: string;
};

type CategoryItemType = {
  id: string;
  title: string;
  category: string;
  descriptions: CategoryItemDescription[];
};

type UploadedFile = {
  id: string;
  name: string;
  classification?: string;
  size?: number;
  type?: string;
};

type CategoryItemProps = {
  item: CategoryItemType;
  files: UploadedFile[];
  caseNumber: string;
  allowedExtensions?: readonly string[];
  maxFileSize?: number;
  selectedFiles?: string[];
  onFileSelectionChange?: (fileId: string, selected: boolean) => void;
};

export const UploadDocumentTypeItem = ({
  item,
  files,
  caseNumber,
  allowedExtensions = FILE_EXTENSIONS.DOCUMENTS,
  maxFileSize = FILE_SIZES.MB_50,
  selectedFiles = [],
  onFileSelectionChange,
}: CategoryItemProps) => {
  const dispatch = useDispatch();

  const { title, descriptions, category } = item;

  const removeFileMutation = useRemoveFile();
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const { uploadFile, isUploading } = useFileUpload({
    allowedExtensions,
    maxFileSize,
  });

  const handleRemoveFile = useCallback(
    (id: string) => {
      // Set the ID of the file being deleted
      setDeletingFileId(id);

      removeFileMutation.mutate(id, {
        onSuccess: () => {
          dispatch(
            setMessage({
              content: "File deleted successfully",
              type: "success",
            })
          );
          // Clear the deleting file ID
          setDeletingFileId(null);
        },
        onError: (error) => {
          dispatch(
            setMessage({
              content: error.message || "Failed to delete file",
              type: "error",
            })
          );
          // Clear the deleting file ID even on error
          setDeletingFileId(null);
        },
      });
    },
    [dispatch, removeFileMutation, setDeletingFileId]
  );

  // Determine whether this document type represents LOI (Letter of Intent)
  // Prefer canonical enum value, fall back to title substring
  const isLOI =
    category === DocumentCategory.LOI || title?.toLowerCase().includes("loi");

  const handleFileUpload = async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;
    // If this is an LOI type, ensure only one LOI exists for this document type
    if (isLOI && files.length > 0) {
      dispatch(
        setMessage({
          content:
            "Only one LOI is allowed. Please remove the existing LOI before uploading a new one.",
          type: "warning",
        })
      );
      return;
    }

    // Determine if multiple file upload should be allowed based on description
    const allowMultiple = descriptions?.[1]?.value
      ?.toLowerCase()
      .includes("you can upload more than one");

    // If not allowing multiple, and already has a file, prevent upload
    if (!allowMultiple && files.length > 0) {
      dispatch(
        setMessage({
          content: "Only one document can be uploaded for this type.",
          type: "warning",
        })
      );
      return;
    }

    // If not allowing multiple, but user selects more than one file, only allow the first
    let filesToUpload = selectedFiles;
    if (!allowMultiple && selectedFiles.length > 1) {
      filesToUpload = [selectedFiles[0]];
      dispatch(
        setMessage({
          content:
            "Only one document can be uploaded for this type. Uploading the first file only.",
          type: "warning",
        })
      );
    }

    // Track successful and failed uploads
    const uploadResults = { success: 0, failed: 0 };

    // Add files to uploading state
    const fileNames = filesToUpload.map((file) => file.name);
    setUploadingFiles(fileNames);

    // Display message for multiple files
    if (filesToUpload.length > 1) {
      dispatch(
        setMessage({
          content: `Uploading ${filesToUpload.length} files`,
          type: "info",
        })
      );
    }

    // Process each selected file
    for (const file of filesToUpload) {
      const uploadFileData = {
        id: `${file.name}-${Date.now()}`,
        file,
        category,
        caseNumber,
      };

      try {
        await uploadFile(uploadFileData);

        // Remove this file from uploading state as soon as it's done
        setUploadingFiles((current) =>
          current.filter((fileName) => fileName !== file.name)
        );

        uploadResults.success++;
      } catch (error) {
        console.error(`Upload error for ${file.name}:`, error);

        // Remove failed file from uploading state
        setUploadingFiles((current) =>
          current.filter((fileName) => fileName !== file.name)
        );

        uploadResults.failed++;

        // Show individual error notification for failed uploads
        dispatch(
          setMessage({
            content: `Failed to upload "${file.name}": ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
            type: "error",
          })
        );
      }
    }

    // Show summary notification for multiple files
    if (filesToUpload.length > 1) {
      if (uploadResults.failed === 0 && uploadResults.success > 0) {
        dispatch(
          setMessage({
            content: `Successfully uploaded ${uploadResults.success} files`,
            type: "success",
          })
        );
      } else if (uploadResults.success > 0) {
        dispatch(
          setMessage({
            content: `Uploaded ${uploadResults.success} files, ${uploadResults.failed} failed`,
            type: "warning",
          })
        );
      }
    }
  };

  const uploadedFileCount = files.length;
  const hasUploadedFiles = uploadedFileCount > 0;

  // Determine if multiple file upload should be allowed based on description
  // LOI should always be single-file regardless of description
  const allowMultiple =
    !isLOI &&
    descriptions?.[1]?.value?.toLowerCase().includes("you can upload more than one");

  return (
    <Card as="section" className="grid grid-cols-[1fr_auto] items-center gap-8 p-4">
      <div className="space-y-2">
        <h3 className="items-center text-lg font-bold">
          {title} <DocumentTypeTooltip description={descriptions} />
        </h3>

        {/* Uploaded File list per Document Type */}
        <div className="space-y-2">
          {!hasUploadedFiles && uploadingFiles.length === 0 && (
            <p className="text-foreground-muted">No files uploaded yet</p>
          )}

          {/* Files currently being uploaded */}
          {uploadingFiles.length > 0 &&
            uploadingFiles.map((fileName, index) => (
              <div
                key={`uploading-${index}`}
                className="flex items-center justify-between rounded-xl bg-background px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <LoadingIcon className="size-5 animate-spin-ccw text-primary-600" />
                  <span className="text-sm font-medium">{fileName}</span>
                </div>
              </div>
            ))}

          {/* Already uploaded files */}
          {hasUploadedFiles &&
            files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between rounded-xl bg-background px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  {onFileSelectionChange && (
                    <Checkbox
                      checked={selectedFiles.includes(file.id)}
                      onCheckedChange={(checked) =>
                        onFileSelectionChange(file.id, !!checked)
                      }
                    />
                  )}
                  <DocumentIcon className="size-5" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block max-w-sm cursor-pointer truncate text-sm font-medium">
                        {stripBundlePrefix(file.name)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="p-2 text-xs">
                      {stripBundlePrefix(file.name)}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  isLoading={deletingFileId === file.id}
                  disabled={!!deletingFileId && deletingFileId !== file.id}
                  onClick={() => handleRemoveFile(file.id)}
                >
                  {deletingFileId !== file.id && <X className="size-4" />}
                </Button>
              </div>
            ))}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center space-y-2">
        <div className="flex items-center gap-1">
          <DocumentIcon className="size-4" />
          <p className="text-2xl font-bold">{uploadedFileCount}</p>
        </div>

        <UploadButton
          onFilesSelected={handleFileUpload}
          accept={allowedExtensions}
          multiple={!!allowMultiple}
          maxSize={maxFileSize}
          disabled={uploadingFiles.length > 0 || isUploading}
          isLoading={isUploading}
        >
          {uploadingFiles.length > 0 ? "Uploading" : "Upload"}
        </UploadButton>
      </div>
    </Card>
  );
};

type DocumentTypeTooltipProps = {
  description: CategoryItemDescription[];
};
const DocumentTypeTooltip = ({ description }: DocumentTypeTooltipProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="inline-block size-4 [&_path]:fill-foreground-muted" />
      </TooltipTrigger>
      <TooltipContent className="max-w-sm space-y-2 text-pretty p-4 text-center">
        {/* Document type information */}
        <p>{description[0]?.value}</p>

        {/* Upload limit */}
        <p className="text-foreground-light-muted">{description[1]?.value}</p>
      </TooltipContent>
    </Tooltip>
  );
};
